import { useMemo } from 'react';

import { FeatureFlag } from '@revelio/core';
import { type SkillLevel, TaxonomyType } from '@revelio/data-access';

import { SKILL_REFERENCE_TAXONOMY_TREE_LEVELS } from '../../../next/CustomDash/filters/utils/skillReferenceTaxonomyTree';
import { useUnleashFlag } from '../../unleash-flags/use-unleash-flag';
import { useSkillTaxonomy } from '../../user-preferences/hooks/useSkillTaxonomySelection';
import { useCustomSkillSelectionList } from './useCustomSkillSelectionList';
import { useTaxonomyTree } from './useTaxonomyTree';

export type DepthToLabelMap = Record<number, string>;

/**
 * Derives a depth -> label mapping for custom skill taxonomy levels from the
 * selection list API response. This avoids storing duplicated metadata on each
 * tree node while still enabling dynamic branch labels and chip titles.
 */
export const useCustomSkillLevelLabels = (
  taxonomyId?: number,
  taxonomyVersionOverride?: string
) => {
  const useReferenceApi = useUnleashFlag(FeatureFlag.ReferenceApi);
  const prefTaxonomyId = useSkillTaxonomy();
  const resolvedTaxonomyId = taxonomyId ?? prefTaxonomyId ?? 0;

  const [{ data }] = useCustomSkillSelectionList(resolvedTaxonomyId, {
    pause: useReferenceApi,
  });

  const { levels: skillTaxonomyReferenceLevels } = useTaxonomyTree({
    taxonomyType: TaxonomyType.Skill,
    taxonomyId: resolvedTaxonomyId,
    taxonomyVersion: taxonomyVersionOverride,
    levels: [...SKILL_REFERENCE_TAXONOMY_TREE_LEVELS],
    pause: !useReferenceApi,
  });

  const labelsByDepth: DepthToLabelMap | null = useMemo(() => {
    if (useReferenceApi) {
      if (skillTaxonomyReferenceLevels.length === 0) return null;
      const sorted = [...skillTaxonomyReferenceLevels].sort(
        (a, b) => a.id - b.id
      );
      const map: DepthToLabelMap = {};
      sorted.forEach((level, index) => {
        const label = level.label ?? level.tag ?? `Level ${level.id}`;
        if (label !== '') map[index] = label;
      });
      return Object.keys(map).length > 0 ? map : null;
    }

    const selectionList = data?.selectionList;
    if (!selectionList) return null;

    const level0 = selectionList.skill_0;
    const level1 = selectionList.skill_1;
    const level2 = selectionList.skill_2;
    const level3 = selectionList.skill_3;

    const safeGet = (lvl: SkillLevel): string | undefined => lvl?.label?.label;

    const map: DepthToLabelMap = {};
    const l0 = safeGet(level0);
    const l1 = safeGet(level1);
    const l2 = safeGet(level2);
    const l3 = level3 ? safeGet(level3 as SkillLevel) : undefined;
    if (l0) map[0] = l0;
    if (l1) map[1] = l1;
    if (l2) map[2] = l2;
    if (l3) map[3] = l3;

    return map;
  }, [data, skillTaxonomyReferenceLevels, useReferenceApi]);

  return { labelsByDepth };
};
